import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiScoreWeightageComponent,
  QaiItemGridComponent,
} from '@qai/ui';
import { IScoreWeight } from 'src/app/interfaces/self-serve/isimulation-score';

@Component({
  selector: 'edit-weighting-modal',
  standalone: true,
  templateUrl: './edit-weighting-modal.component.html',
  styleUrls: ['./edit-weighting-modal.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    QaiButtonComponent,
    QaiDialogComponent,
    QaiScoreWeightageComponent,
    QaiItemGridComponent,
  ],
})
export class EditWeightingModalComponent {
  @ViewChild('dialog') dialogRef: QaiDialogComponent | undefined;
  @Input('open') open: boolean = false;
  @Input() weights: IScoreWeight[] = [];
  @Output() confirm = new EventEmitter<IScoreWeight[]>();
  @Output() closeModal = new EventEmitter<void>();

  localWeights: IScoreWeight[] = [];
  errorMessage: string = '';
  fieldErrors: { [key: string]: boolean } = {};

  description: string =
    'The overall score will be generated from the weighted average of the following sections. All enabled section weights must add upto 100%. The skills section cannot have a weight greater than 20%.';

  ngOnChanges(changes: SimpleChanges) {
    if (this.weights.length > 0) {
      this.localWeights = this.weights.map((w) => ({ ...w }));
    }

    if (changes['open']?.currentValue) {
      this.errorMessage = '';
      this.showModal();
    }
  }

  areWeightsValid(): boolean {
    this.fieldErrors = {};
    this.errorMessage = '';

    const totalWeight = this.localWeights.reduce((sum, weight) => {
      return weight.value !== 0 ? sum + Number(weight.value) : sum;
    }, 0);

    const allDisabled = this.localWeights.every(
      (weight) => weight.disabled === true
    );
    if (allDisabled) {
      this.errorMessage = 'At least one scoring action must be enabled';
      return false;
    }

    let hasErrors = false;

    this.localWeights.forEach((weight, index) => {
      const fieldKey = `weight-${index}`;
      const value = Number(weight.value);

      if (weight.value === 0) {
        this.fieldErrors[fieldKey] = false;
        return;
      }

      if (value < 0 || value > 100) {
        this.fieldErrors[fieldKey] = true;
        this.errorMessage = 'Section weight must be between 0 and 100';
        hasErrors = true;
      } else if (weight.section === 'Skills' && value > 20) {
        this.fieldErrors[fieldKey] = true;
        this.errorMessage =
          'The skills section cannot have a weight greater than 20%';
        hasErrors = true;
      } else {
        this.fieldErrors[fieldKey] = false;
      }
    });

    if (!hasErrors && Math.round(totalWeight) !== 100) {
      this.errorMessage = `The total section weight must be 100%`;
      hasErrors = true;
    }

    return !hasErrors;
  }

  handleConfirm() {
    if (!this.areWeightsValid()) {
      return;
    }

    const updatedWeights = this.localWeights.map((weight) => ({
      ...weight,
    }));
    this.confirm.emit(updatedWeights);
    this.hideModal();
  }

  handleClose() {
    this.closeModal.emit();
  }

  hideModal() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  showModal() {
    if (this.dialogRef) {
      this.dialogRef.showModal();
    }
  }

  onDisableChange(checked: boolean, i: number) {
    if (checked) {
      // Enable the section
      this.localWeights[i].value = this.weights?.[i]?.value || 1;
      this.localWeights[i].disabled = false;
    } else {
      // Disable the section
      this.localWeights[i].value = 0;
      this.localWeights[i].disabled = true;
    }
    this.areWeightsValid();
  }

  onWeightInputChange(value: number, index: number) {
    this.localWeights[index].value = value;
    this.areWeightsValid();
  }

  hasErrors(): boolean {
    return (
      Object.values(this.fieldErrors).some((val) => val === true) ||
      this.errorMessage !== ''
    );
  }
}
